import { useMemo } from "react";
import { ErrorMessage } from "@hireart/component-library";
import { usePostHog } from "posthog-js/react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { sendToPosthogError } from "./analytics/posthog-analytics";
import { BugsnagErrorBoundary } from "./BugsnagErrorBoundary";
import { mapBugsnagEventToPosthogErrorPayload } from "./util";

const withProps = (Component, additionalProps) => {
  // eslint-disable-next-line react/prop-types -- ignoring Bugsnag's clearError (on purpose, so it doesn't end up in the DOM output) so defining propTypes for it is unnecessary
  return ({ clearError, ...rest }) => (
    <Component {...rest} {...additionalProps} />
  );
};

export const ErrorBoundary = ({ children, className = undefined }) => {
  const eventName = "error_boundary";
  const errorId = useMemo(() => uuidv4(), []);
  const extraContext = useMemo(
    () => ({
      errorId,
      source: "frontend-ui",
    }),
    [errorId]
  );
  const postHog = usePostHog();

  const onError = (event) => {
    event.addMetadata("HireArt", extraContext);
    const posthogPayload = {
      ...mapBugsnagEventToPosthogErrorPayload(event),
      ...extraContext,
    };

    sendToPosthogError(postHog, eventName, posthogPayload);
  };

  const FallbackComponent = useMemo(
    () => withProps(ErrorMessage, { className }),
    [className]
  );

  return (
    <BugsnagErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={onError}
    >
      {children}
    </BugsnagErrorBoundary>
  );
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
